import { ChangeDetectionStrategy, Component, effect, input } from '@angular/core';
import { SearchPopupHandlerComponent } from '@shared/components/popup/base/search-popup-handler.component';
import {
  AbstractControl,
  ControlContainer,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { FlexModule } from '@ngbracket/ngx-layout';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import { MatRipple } from '@angular/material/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TranslateModule } from '@ngx-translate/core';
import { AutofocusDirective } from '@shared/directives/autofocus.directive';

/** `T` is the option type, `V` the (possibly mapped) form value — see SearchPopupHandlerComponent. */
@Component({
  selector: 'recrewt-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectComponent,
    },
  ],
  imports: [
    FlexModule,
    MatFormField,
    ReactiveFormsModule,
    MatLabel,
    MatError,
    MatInput,
    NgTemplateOutlet,
    MatIcon,
    MatSuffix,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    MatRipple,
    NgxSkeletonLoaderModule,
    AsyncPipe,
    TranslateModule,
    AutofocusDirective,
  ],
})
export class SelectComponent<T = any, V = T> extends SearchPopupHandlerComponent<T, V> {
  readonly matStyle = input(false);

  readonly placeholder = input('');

  readonly parentFormGroup = input<AbstractControl>();

  readonly error = input('');

  readonly require = input(false);

  readonly isInvalid = input(false);

  readonly selectControlName = 'select';

  readonly selectFormGroup = new FormGroup({
    [this.selectControlName]: new FormControl<string | null>(null),
  });

  constructor() {
    super();
    // Keeps the mat-style display input in sync with the selection — covers both user
    // selects and programmatic writeValue() calls.
    effect(() => {
      const label = this.displayValue(this.selectedOption());
      const display =
        typeof label === 'string' && label.length ? this.translate.instant(label) : '';
      this.selectControl.setValue(display, { emitEvent: false });
    });

    effect(() => {
      if (this.matStyle()) {
        this.yOffset = -24;
      }
    });
  }

  private get selectControl(): FormControl<string | null> {
    return this.selectFormGroup.controls[this.selectControlName] as FormControl<string | null>;
  }

  override close(): void {
    super.close();
    if (!this.parentFormGroup()) {
      return;
    }
    this.selectControl.markAsTouched();
  }
}
